
import React, { useState, createContext, useContext } from 'react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { EntityType, useModal } from '@/contexts/ModalContext';

// DataTable context type
type DataTableContextType = {
  variant?: 'default' | 'compact';
  bordered?: boolean;
  scrollable?: boolean;
  hoverable?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
};

// Create the DataTable context
const DataTableContext = createContext<DataTableContextType>({
  variant: 'default',
  bordered: true,
  scrollable: true,
  hoverable: true,
});

// Column type definition
export interface DataTableColumn {
  id: string;
  label: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  className?: string;
  width?: string;
}

// Root DataTable component
interface DataTableProps extends React.HTMLAttributes<HTMLDivElement>, DataTableContextType {}

const DataTableRoot = React.forwardRef<HTMLDivElement, DataTableProps>(
  (
    {
      children,
      className,
      variant = 'default',
      bordered = true,
      scrollable = true,
      hoverable = true,
      sortColumn,
      sortDirection,
      onSort,
      ...props
    },
    ref
  ) => {
    // Provide context values to all child components
    const contextValue: DataTableContextType = {
      variant,
      bordered,
      scrollable,
      hoverable,
      sortColumn,
      sortDirection,
      onSort,
    };

    return (
      <DataTableContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn(
            'w-full h-full overflow-hidden rounded-xl shadow-sm bg-card flex flex-col',
            className
          )}
          {...props}
        >
          <div className={cn('relative flex-1 min-h-0', scrollable && 'overflow-x-auto')}>
            <Table className="h-full">
              {children}
            </Table>
          </div>
        </div>
      </DataTableContext.Provider>
    );
  }
);

DataTableRoot.displayName = 'DataTable';

// Header component
interface HeaderProps {
  columns: DataTableColumn[];
}

const DataTableHeader: React.FC<HeaderProps> = ({ columns }) => {
  const { variant, sortColumn, sortDirection, onSort } = useContext(DataTableContext);
  
  const handleSort = (column: DataTableColumn) => {
    if (!column.sortable || !onSort) return;
    onSort(column.id);
  };

  return (
    <TableHeader>
      <TableRow>
        {columns.map((column) => (
          <TableHead
            key={column.id}
            className={cn(
              'px-4 py-3 text-xs uppercase text-muted-foreground font-medium',
              column.align === 'center' && 'text-center',
              column.align === 'right' && 'text-right',
              column.sortable && 'cursor-pointer select-none',
              column.id === 'actions' && 'sticky right-0 z-10',
              column.className,
              variant === 'compact' && 'py-2'
            )}
            onClick={() => column.sortable && handleSort(column)}
            style={column.width ? { width: column.width } : undefined}
          >
            <div className={cn(
              "flex items-center gap-1",
              column.align === 'right' && 'justify-end',
              column.align === 'center' && 'justify-center'
            )}>
              {column.label}
              {column.sortable && sortColumn === column.id && (
                <span className="ml-1">
                  {sortDirection === 'asc' ? (
                    <ChevronUp className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </span>
              )}
            </div>
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  );
};

// Row component
interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  entityType?: EntityType;
  entityId?: string;
  onClick?: () => void;
}

const DataTableRow: React.FC<RowProps> = ({ 
  children, 
  className, 
  entityType,
  entityId,
  onClick, 
  ...props 
}) => {
  const { hoverable, variant } = useContext(DataTableContext);
  const { openModal } = useModal();
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (entityType && entityId) {
      openModal(entityType, entityId);
    }
  };
  
  return (
    <TableRow
      className={cn(
        'border-b border-border transition-colors',
        hoverable && 'hover:bg-muted/40',
        
        (onClick || (entityType && entityId)) && 'cursor-pointer',
        variant === 'compact' && 'h-10',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
    </TableRow>
  );
};

// Cell component
interface CellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
}

const DataTableCell: React.FC<CellProps> = ({ children, className, ...props }) => {
  const { variant } = useContext(DataTableContext);
  
  return (
    <TableCell
      className={cn(
        'px-4 py-3 text-foreground',
        variant === 'compact' && 'py-2',
        className
      )}
      {...props}
    >
      {children}
    </TableCell>
  );
};

// Actions component
interface ActionsProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
}

const DataTableActions: React.FC<ActionsProps> = ({ children, className, ...props }) => {
  const { variant } = useContext(DataTableContext);
  
  return (
    <TableCell
      className={cn(
        'px-4 py-3 text-right',
        variant === 'compact' && 'py-2',
        className
      )}
      {...props}
    >
      <div className="flex justify-end space-x-1">
        {children}
      </div>
    </TableCell>
  );
};

// Empty state component
interface EmptyProps {
  colSpan: number;
  message?: string;
  children?: React.ReactNode;
}

const DataTableEmpty: React.FC<EmptyProps> = ({ colSpan, message = 'No data found', children }) => {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-8 text-center text-muted-foreground">
        {children || message}
      </TableCell>
    </TableRow>
  );
};

// Loading state component
interface LoadingProps {
  colSpan: number;
  message?: string;
}

const DataTableLoading: React.FC<LoadingProps> = ({ colSpan, message = 'Loading...' }) => {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-8 text-center text-muted-foreground">
        {message}
      </TableCell>
    </TableRow>
  );
};

// Attach subcomponents to the DataTable component using Object.assign
const DataTable = Object.assign(DataTableRoot, {
  Header: DataTableHeader,
  Row: DataTableRow,
  Cell: DataTableCell,
  Actions: DataTableActions,
  Empty: DataTableEmpty,
  Loading: DataTableLoading
});

export { DataTable, TableBody };